import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Collapse,
  Space,
  Input,
  Form,
  Select,
  Checkbox,
  Radio,
  Flex,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AddClientApi, EditClientApi, GetClientByIdApi } from "../ClientsApi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import CountryCode from "CommonComponents/CountryCode";
import Address from "CommonComponents/Address";
import AddressType from "features/Enums/AddressType";

function AddClient(props) {
  const { TextArea } = Input;
  const { Panel } = Collapse;
  const form = Form.useForm()[0];
  const dispatch = useDispatch();
  const { id } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const leadData = location.state || {};
  const client = useSelector((state) => state.clients.clientListById);
  const companies = useSelector((state) => state.lookup.companyNames);
  const [sameAsCompany, setSameAsCompany] = useState(
    (props.leadType || "Residential") === "Residential"
  );
  const [companyAddressId, setCompanyAddressId] = useState(null);
  const [billingAddressId, setBillingAddressId] = useState(null);
  const [isFieldsEdited, setIsFieldsEdited] = useState(false);
  const [leadType, setLeadType] = useState(props.leadType || "Residential");
  const [showAlternateFields, setShowAlternateFields] = useState(
    leadType === "Commercial"
  );
  const isLeadTypeFixed = !!props.leadType;
  useEffect(() => {
    if (props.clientId) {
      dispatch(GetClientByIdApi(props.clientId));
    } else {
      setTimeout(() => {
        form.resetFields();
        const isResidential =
          (props.leadType || "Residential") === "Residential";
        form.setFieldsValue({
          leadType: props.leadType || "Residential",
          isBillingAddressSame: isResidential,
        });
        setSameAsCompany(isResidential);
        setLeadType(props.leadType || "Residential");
        setShowAlternateFields(false);
      }, 0);
    }
  }, [props.clientId]);

  useEffect(() => {
    if (client && Object.keys(client).length > 0) {
      if (Array.isArray(client?.address) && client?.address?.length > 0) {
        const addressList = client.address;
        const companyAddress =
          addressList.find(
            (addr) => addr.addressType === AddressType.CompanyAddress.id
          ) || {};
        const billingAddress =
          addressList.find(
            (addr) => addr.addressType === AddressType.BillingAddress.id
          ) || {};
        const additionalAddresses = addressList.filter(
          (addr) => addr.addressType === AddressType.AdditionalAddress.id
        );
        const LeadType = client.isResidential ? "Residential" : "Commercial";
        setLeadType(LeadType);

        form.setFieldsValue({
          ...client,
          Company: companyAddress,
          Billing: billingAddress,
          address: additionalAddresses,
          isBillingAddressSame: client.isBillingAddressSame,
          leadType: LeadType,
          mappedcompanyname:
            client.clientCompanyMapping?.map((item) => item.companyId) || [],
          companyCountryCode:
            client.companyCountryCode != null
              ? client.companyCountryCode
              : "Cell",
          alternateCountryCode:
            client.alternateCountryCode != null
              ? client.alternateCountryCode
              : "Cell",
        });

        setSameAsCompany(client.isBillingAddressSame);
        setShowAlternateFields(
          !!client.alternateName ||
            !!client.alternateEmail ||
            !!client.alternatePhone
        );
      } else {
        form.setFieldsValue(client);
        setSameAsCompany(client.isBillingAddressSame);
        setShowAlternateFields(
          !!client.alternateName ||
            !!client.alternateEmail ||
            !!client.alternatePhone
        );
      }
    }
  }, [client, form]);

  const handleSubmit = async (formData) => {
    let addresses = [];
    if (Array.isArray(formData.address)) {
      addresses = formData.address.map((addr) => ({
        ...addr,
        isBilling: addr.isBilling || false,
        isCheckedSame: false,
        addressType: AddressType.AdditionalAddress.id,
      }));
    }
    if (formData.Company) {
      addresses.push({
        ...formData.Company,
        isBilling: false,
        isCheckedSame: sameAsCompany,
        addressType: AddressType.CompanyAddress.id,
      });
    }
    if (formData.Billing) {
      addresses.push({
        ...formData.Billing,
        isBilling: true,
        isCheckedSame: sameAsCompany,
        addressType: AddressType.BillingAddress.id,
      });
    }

    const transformedData = {
      ...formData,
      isActive: formData.isActive ?? true,
      isBillingAddressSame: sameAsCompany,
      address: addresses,
      isResidential: formData.leadType == "Residential" ? true : false,
      isCommercial: formData.leadType == "Commercial" ? true : false,
      partnerClientId: props.partnerClientId || null,
    };
    if (props.clientId) {
      await Promise.all([
        dispatch(EditClientApi(transformedData, props.pagination)).then(
          (res) => {
            if (res) {
              oncancel();
            }
          }
        ),
      ]);
    } else {
      await Promise.all([
        dispatch(AddClientApi(transformedData, props.pagination)).then(
          (res) => {
            if (res) {
              if (props?.onSuccess) {
                props?.onSuccess(res);
              }
              oncancel();
            }
            props.onClose();
          }
        ),
      ]);
    }
  };
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setSameAsCompany(isChecked);

    if (isChecked) {
      const companyAddress = form.getFieldValue("Company") || {};
      form.setFieldsValue({
        Billing: {
          address1: companyAddress.address1,
          address2: companyAddress.address2,
          city: companyAddress.city,
          provinceId: companyAddress.provinceId,
          zip: companyAddress.zip,
          countryId: companyAddress.countryId,
        },
        isBillingAddressSame: true,
      });
    } else {
      form.setFieldsValue({
        isBillingAddressSame: false,
      });
    }
  };
  const handleAddressChange = () => {
    if (leadType === "Commercial" && sameAsCompany) {
      setSameAsCompany(false);
    }
    if (leadType === "Residential" && sameAsCompany) {
      const companyAddress = form.getFieldValue("Company") || {};
      form.setFieldsValue({
        Billing: {
          address1: companyAddress.address1,
          address2: companyAddress.address2,
          city: companyAddress.city,
          provinceId: companyAddress.provinceId,
          zip: companyAddress.zip,
          countryId: companyAddress.countryId,
        },
      });
    }
  };

  const oncancel = () => {
    props.onClose();
    if (!props.clientId) {
      form.resetFields();
      const isResidential = (props.leadType || "Residential") === "Residential";
      form.setFieldsValue({
        leadType: props.leadType || "Residential",
        isBillingAddressSame: isResidential,
      });
      setSameAsCompany(isResidential);
      setLeadType(props.leadType || "Residential");
      setShowAlternateFields(false);
    }
  };

  const onPopCancel = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: props.clientId
          ? "AddClient.Popup.CancelEditClient"
          : "AddClient.Popup.CancelAddClient",
      }),
      content: intl.formatMessage({
        id: "Common.PopConfirm",
      }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      cancelText: intl.formatMessage({ id: "Common.No" }),
      onOk: () => {
        oncancel();
      },
    });
  };

  useEffect(() => {
    if (props.leadType) {
      setLeadType(props.leadType);
      form.setFieldsValue({ leadType: props.leadType });
      setSameAsCompany(props.leadType === "Residential");
      form.setFieldValue(
        "isBillingAddressSame",
        props.leadType === "Residential"
      );
    }
  }, [props.leadType]);
  useEffect(() => {
    if (leadType === "Residential" && sameAsCompany) {
      const companyAddress = form.getFieldValue("Company") || {};
      form.setFieldsValue({
        Billing: {
          address1: companyAddress.address1,
          address2: companyAddress.address2,
          city: companyAddress.city,
          provinceId: companyAddress.provinceId,
          zip: companyAddress.zip,
          countryId: companyAddress.countryId,
        },
      });
    }
  }, [leadType, sameAsCompany, form.getFieldValue("Company")]);
  return (
    <>
      <Form
        form={form}
        name="basic"
        layout="vertical"
        autoComplete="off"
        size="large"
        onFinish={handleSubmit}
        initialValues={{
          leadType: props.leadType || "Residential",
          isBillingAddressSame:
            (props.leadType || "Residential") === "Residential",
        }}
      >
        <Form.Item name="id" hidden={true}></Form.Item>
        <Form.Item name="createdBy" hidden={true}></Form.Item>
        <Form.Item name="createdDate" hidden={true}></Form.Item>
        <Collapse defaultActiveKey={["1", "2"]} size="small">
          <Panel header={<FormattedMessage id="Common.BasicInfo" />} key="1">
            <Row gutter={16}>
              <Col xs={24} sm={24}>
                <Form.Item
                  label={<FormattedMessage id="AddClient.ClientType" />}
                  name="leadType"
                >
                  <Radio.Group
                    value={leadType}
                    onChange={(e) => {
                      const selected = e.target.value;
                      if (!isLeadTypeFixed) {
                        setLeadType(selected);
                        form.setFieldValue("leadType", selected);
                        if (!props.clientId) {
                          const isResidential = selected === "Residential";
                          setSameAsCompany(isResidential);
                          form.setFieldValue(
                            "isBillingAddressSame",
                            isResidential
                          );
                        }
                        setShowAlternateFields(selected === "Commercial");
                      }
                    }}
                    options={[
                      {
                        value: "Residential",
                        label: (
                          <Flex
                            gap="small"
                            justify="center"
                            align="center"
                            vertical
                          >
                            <FormattedMessage id="Lead.Residential" />
                          </Flex>
                        ),
                        disabled:
                          isLeadTypeFixed && props.leadType !== "Residential",
                      },
                      {
                        value: "Commercial",
                        label: (
                          <Flex
                            gap="small"
                            justify="center"
                            align="center"
                            vertical
                          >
                            <FormattedMessage id="Lead.Commercial" />
                          </Flex>
                        ),
                        disabled:
                          isLeadTypeFixed && props.leadType !== "Commercial",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>

              {leadType === "Commercial" ? (
                <>
                  {/* Company Info */}
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.CompanyName" />}
                      name="companyName"
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "AddClient.error.PleaseEnterComapanyName",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterCompanyName",
                        })}
                        maxLength={100}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.CompanyWebsite" />}
                      name="companyWebsite"
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterCompanyWebsite",
                        })}
                        maxLength={100}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.CompanyEmail" />}
                      name="companyEmail"
                      rules={[
                        {
                          type: "email",
                          message: intl.formatMessage({
                            id: "Lead.Rule.Email",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterCompanyEmail",
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={
                        <FormattedMessage id="AddClient.OfficePhoneNumber" />
                      }
                      name="companyPhone"
                      rules={[
                        {
                          pattern: /^[0-9]{10,11}$/,
                          message: intl.formatMessage({
                            id: "AddClient.error.Phonenumbermustbe10digits",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterOfficePhoneNumber",
                        })}
                        maxLength={11}
                        type="tel"
                        value={form.getFieldValue("phone")}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          form.setFieldsValue({ companyPhone: value });
                        }}
                        addonBefore={<CountryCode name="companyCountryCode" />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.HSTNumber" />}
                      name="gstHstnumber"
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterHstNumber",
                        })}
                        maxLength={20}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.QSTNumber" />}
                      name="qstNumber"
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterQstNumber",
                        })}
                        maxLength={20}
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : null}

              {/* Main Contact Info */}
              {leadType == "Commercial" && (
                <Col xs={24}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#3a5892",
                      margin: "0px 0 4px 0",
                    }}
                  >
                    <FormattedMessage id="AddClient.MainContactInfo" />
                  </div>
                </Col>
              )}

              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Form.Item
                  label={
                    leadType === "Commercial" ? (
                      <FormattedMessage id="AddClient.Name" />
                    ) : (
                      <FormattedMessage id="AddClient.ContactPerson" />
                    )
                  }
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id:
                          leadType === "Commercial"
                            ? "AddClient.PleaseEnterName"
                            : "AddClient.error.PleaseEnterContactPerson",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id:
                        leadType === "Commercial"
                          ? "AddClient.Placeholder.EnterName"
                          : "AddClient.placeholder.EnterContactPerson",
                    })}
                    maxLength={50}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Form.Item
                  label={<FormattedMessage id="AddClient.EmailId" />}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "AddClient.error.PleaseEnterEmailId",
                      }),
                    },
                    {
                      type: "email",
                      message: intl.formatMessage({
                        id: "Lead.Rule.Email",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "AddClient.placeholder.EnterEmailId",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Form.Item
                  label={<FormattedMessage id="AddClient.PhoneNumber" />}
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "AddClient.error.PleaseEnterPhoneNumber",
                      }),
                    },
                    // {
                    //   len: 10,
                    //   message: intl.formatMessage({
                    //     id: "AddClient.error.Phonenumbermustbe10digits",
                    //   }),
                    // },
                    {
                      pattern: /^[0-9]{10,11}$/,
                      message: intl.formatMessage({
                        id: "AddClient.error.Phonenumbermustbe10digits",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "AddClient.placeholder.EnterPhoneNumber",
                    })}
                    addonBefore={<CountryCode name="countryCode" />}
                    style={{
                      width: "100%",
                    }}
                    maxLength={11}
                    type="tel"
                    value={form.getFieldValue("phone")}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      form.setFieldsValue({ phone: value });
                    }}
                    onPaste={(event) => {
                      const pasteData = event.clipboardData.getData("Text");
                      if (!/^\d+$/.test(pasteData)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>

              {leadType === "Residential" && !showAlternateFields && (
                <Col xs={24}>
                  <a
                    href="#"
                    className="text-dark"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAlternateFields(true);
                    }}
                    style={{
                      fontSize: "12px",
                      color: "#1890ff",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <PlusCircleOutlined style={{ marginRight: "5px" }} />
                    <FormattedMessage id="AddClient.AlternateContactInfo" />
                  </a>
                </Col>
              )}

              {(leadType === "Commercial" || showAlternateFields) && (
                <>
                  <Col xs={24}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#3a5892",
                        margin: "0px 0 4px 0",
                      }}
                    >
                      {leadType === "Commercial" ? (
                        <FormattedMessage id="AddClient.AccountingContactInfo" />
                      ) : (
                        <FormattedMessage id="AddClient.AlternateContactInfo" />
                      )}
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={
                        leadType === "Commercial" ? (
                          <FormattedMessage id="AddLead.AccountantName" />
                        ) : (
                          <FormattedMessage id="AddLead.AlternateName" />
                        )
                      }
                      name="alternateName"
                    >
                      <Input
                        placeholder={
                          leadType === "Commercial"
                            ? intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAccountantName",
                              })
                            : intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAlternateName",
                              })
                        }
                        maxLength={50}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={
                        leadType === "Commercial" ? (
                          <FormattedMessage id="AddLead.AccountantEmail" />
                        ) : (
                          <FormattedMessage id="AddLead.AlternateEmail" />
                        )
                      }
                      name="alternateEmail"
                      rules={[
                        {
                          type: "email",
                          message: intl.formatMessage({
                            id: "Lead.Rule.Email",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={
                          leadType === "Commercial"
                            ? intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAccountantEmail",
                              })
                            : intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAlternateEmail",
                              })
                        }
                        maxLength={50}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={
                        leadType === "Commercial" ? (
                          <FormattedMessage id="AddLead.AccountantPhoneNumber" />
                        ) : (
                          <FormattedMessage id="AddLead.AlternatePhoneNumber" />
                        )
                      }
                      name="alternatePhone"
                      rules={[
                        {
                          pattern: /^[0-9]{10,11}$/,
                          message: intl.formatMessage({
                            id: "AddClient.error.Phonenumbermustbe10digits",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={
                          leadType === "Commercial"
                            ? intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAccountantPhoneNumber",
                              })
                            : intl.formatMessage({
                                id: "AddLead.Placeholder.EnterAlternatePhoneNumber",
                              })
                        }
                        addonBefore={
                          <CountryCode name="alternateCountryCode" />
                        }
                        style={{ width: "100%" }}
                        maxLength={11}
                        type="tel"
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          form.setFieldsValue({ alternatePhone: value });
                        }}
                        onPaste={(event) => {
                          const pasteData = event.clipboardData.getData("Text");
                          if (!/^\d+$/.test(pasteData)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              {props?.clientId ? (
                <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                  <Form.Item
                    label={<FormattedMessage id="AddClient.Status" />}
                    name="isActive"
                    rules={[{ required: true, message: "Status is required" }]}
                    initialValue={true}
                  >
                    <Select
                      placeholder={intl.formatMessage({
                        id: "AddClient.placeholder.SelectStatus",
                      })}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const text = intl.formatMessage({
                          id: option.value
                            ? "Common.Active"
                            : "Common.InActive",
                        });
                        return text.toLowerCase().includes(input.toLowerCase());
                      }}
                    >
                      <Select.Option value={true}>
                        {<FormattedMessage id="Common.Active" />}
                      </Select.Option>
                      <Select.Option value={false}>
                        {<FormattedMessage id="Common.InActive" />}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              ) : null}
            </Row>
          </Panel>
          <Panel
            header={<FormattedMessage id="Common.AddressInfo" />}
            key="2"
            forceRender
          >
            <Row gutter={16}>
              <Col span={24}>
                <h5 className="pb-10">
                  {leadType === "Residential" ||
                  props.leadType == "Residential" ? (
                    <FormattedMessage id="AddClient.ResidentialAddress" />
                  ) : (
                    <FormattedMessage id="AddClient.CompanyAddress" />
                  )}
                </h5>
                <Address
                  className="mt-10"
                  prefix="Company"
                  onChange={handleAddressChange}
                  setCompanyAddressId={setCompanyAddressId}
                  setIsFieldsEdited={setIsFieldsEdited}
                  isValidationRequired={props?.isValidationRequired}
                />
              </Col>

              <Col span={24}>
                <Form.List name="address">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div
                          key={key}
                          style={{
                            marginBottom: "16px",
                            padding: "10px",
                            border: "1px solid #ddd",
                          }}
                        >
                          <Address
                            prefix={name}
                            setIsFieldsEdited={setIsFieldsEdited}
                          />
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              remove(name);
                            }}
                            style={{
                              fontSize: "14px",
                              color: "#ff4d4f",
                              display: "block",
                              marginTop: "10px",
                            }}
                          >
                            <MinusCircleOutlined
                              style={{ marginRight: "5px" }}
                            />
                            {<FormattedMessage id="AddClient.RemoveAddress" />}
                          </a>
                        </div>
                      ))}
                      <a
                        href="#"
                        className="text-dark"
                        onClick={(e) => {
                          e.preventDefault();
                          add({ id: 0, isBilling: false });
                        }}
                        style={{
                          fontSize: "12px",
                          color: "#1890ff",
                        }}
                      >
                        <PlusCircleOutlined style={{ marginRight: "5px" }} />
                        {<FormattedMessage id="AddClient.AddMoreAddress" />}
                      </a>
                    </>
                  )}
                </Form.List>
              </Col>

              <Col span={24} className="mt-10">
                <h5 className="pb-10">
                  {<FormattedMessage id="AddClient.BillingAddress" />}
                </h5>
                <Checkbox
                  className="pb-10"
                  checked={sameAsCompany}
                  onChange={handleCheckboxChange}
                >
                  {<FormattedMessage id="AddClient.SameAsCompanyAddress" />}
                </Checkbox>
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.ContactPerson" />}
                      name="billingCpname"
                      // rules={[
                      //   {
                      //     required: leadType == "Commercial" ? true : false,
                      //     message: intl.formatMessage({
                      //       id: "AddClient.error.PleaseEnterContactPerson",
                      //     }),
                      //   },
                      // ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterContactPerson",
                        })}
                        maxLength={50}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label={<FormattedMessage id="AddClient.PhoneNumber" />}
                      name="billingCpphone"
                      rules={[
                        // {
                        //   required: leadType == "Commercial" ? true : false,
                        //   message: intl.formatMessage({
                        //     id: "AddClient.error.PleaseEnterPhoneNumber",
                        //   }),
                        // },
                        {
                          pattern: /^[0-9]{10,11}$/,
                          message: intl.formatMessage({
                            id: "AddClient.error.Phonenumbermustbe10digits",
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          id: "AddClient.placeholder.EnterPhoneNumber",
                        })}
                        maxLength={11}
                        type="tel"
                        value={form.getFieldValue("phone")}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          form.setFieldsValue({ billingCpphone: value });
                        }}
                        onPaste={(event) => {
                          const pasteData = event.clipboardData.getData("Text");
                          if (!/^\d+$/.test(pasteData)) {
                            event.preventDefault();
                          }
                        }}
                        addonBefore={
                          <CountryCode name="billingCpcountryCode" />
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Address
                  className="mt-10"
                  prefix="Billing"
                  disabled={sameAsCompany}
                  setBillingAddressId={setBillingAddressId}
                  setIsFieldsEdited={setIsFieldsEdited}
                  isValidationRequired={props?.isValidationRequired}
                />
              </Col>
            </Row>
          </Panel>
        </Collapse>
        <Row gutter={16}>
          <Col span={24} className="text-center mt-10">
            <Space>
              <Button
                danger
                variant="outlined"
                size="medium"
                onClick={onPopCancel}
              >
                <CloseCircleOutlined />
                {<FormattedMessage id="AddClient.Cancel" />}
              </Button>
              <Button type="primary" size="medium" htmlType="submit">
                <CheckCircleOutlined />
                {props.clientId ? (
                  <FormattedMessage id="AddClient.SaveChanges" />
                ) : (
                  <FormattedMessage id="AddClient.AddClient" />
                )}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default AddClient;
